import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { hireRead, HIRE_ACCESS_CHANGED } from '@/lib/hireApi';
import type { HireCapabilities } from '@/types/hire';
const denied: HireCapabilities = {
  canView: false,
  canApprove: false,
  canManage: false,
  canManageJobs: false,
  canReviewApplicants: false,
};
export function useHireCapabilities() {
  const { user, session } = useAuth();
  const { pathname } = useLocation();
  const authKey = user && session ? user.id : '';
  const token = session?.access_token;
  const [epoch, setEpoch] = useState(0);
  const [result, setResult] = useState<{ key: string; data: HireCapabilities; error: boolean } | null>(null);
  const refresh = useCallback(() => setEpoch((n) => n + 1), []);
  // Discard prior-user state during render so logout/relogin cannot reuse it.
  if (result && result.key !== authKey) setResult(null);
  useEffect(() => {
    if (!authKey) return;
    const controller = new AbortController();
    hireRead<HireCapabilities>('/capabilities', controller.signal)
      .then((data) => {
        if (!controller.signal.aborted)
          setResult({
            key: authKey,
            data: Object.fromEntries(
              Object.keys(denied).map((k) => [k, data[k as keyof HireCapabilities] === true]),
            ) as unknown as HireCapabilities,
            error: false,
          });
      })
      .catch(() => {
        if (!controller.signal.aborted) setResult({ key: authKey, data: denied, error: true });
      });
    // Keep drafts mounted during ordinary same-user revalidation, but an API
    // authority failure is already evidence to revoke access immediately.
    const revoke = () => {
      controller.abort();
      setResult({ key: authKey, data: denied, error: true });
      refresh();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener(HIRE_ACCESS_CHANGED, revoke);
    const timer = window.setInterval(refresh, 60_000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener(HIRE_ACCESS_CHANGED, revoke);
    };
  }, [authKey, token, pathname, epoch, refresh]);
  const current = authKey && result?.key === authKey ? result : null;
  return {
    ...(current?.data ?? denied),
    loading: Boolean(authKey && !current),
    error: current?.error ?? false,
    refresh,
  };
}
